import React from 'react';
import { X, FileText, MapPin, Book, Bot, HelpCircle } from 'lucide-react';
import { useTranslation } from "react-i18next";

const SourceInfoModal = ({ isOpen, onClose, post }) => {
  const { t } = useTranslation();
  if (!isOpen || !post) return null;

  // Extract real source data from post
  const pageInfo = post.source_page_start || post.page_number || post.position_data?.estimated_page || post.payload?.reference?.page;
  const lineStartInfo = post.source_line_start || post.line_number || post.position_data?.line;
  const lineEndInfo = post.source_line_end;
  const reference = post.payload?.reference;
  const chapterInfo = post.position_data?.chapter_title || reference?.chapter;

  // Helper to format location info
  const getLocationText = () => {
    // Check for sentinel values (1-21 pattern)
    const isSentinelData = lineStartInfo === 1 && lineEndInfo === 21;
    
    // Use source_ref if it's credible
    if (post.source_ref && 
        !post.source_ref.includes('Lines 1-21') && 
        !post.source_ref.includes('Line 1-21') &&
        !(post.source_ref.includes('Page 1') && post.source_ref.includes('Lines 1-'))) {
      return post.source_ref;
    }
    
    // Build from components if not sentinel data
    if (!isSentinelData) {
      const chapter = post.chapter_index || post.chapter_number;
      if (lineStartInfo && lineEndInfo && lineStartInfo !== lineEndInfo) {
        const prefix = chapter ? `${t('carousel.source_modal.ch')} ${chapter} - ` : '';
        return `${prefix}${t('carousel.source_modal.page')} ${pageInfo}, ${t('carousel.source_modal.line')}s ${lineStartInfo}-${lineEndInfo}`;
      } else if (lineStartInfo) {
        const prefix = chapter ? `${t('carousel.source_modal.ch')} ${chapter} - ` : '';
        return `${prefix}${t('carousel.source_modal.page')} ${pageInfo}, ${t('carousel.source_modal.line')} ${lineStartInfo}`;
      }
    }
    
    // Fallback to page only
    if (pageInfo) {
      const chapter = post.chapter_index || post.chapter_number;
      const prefix = chapter ? `${t('carousel.source_modal.ch')} ${chapter} - ` : '';
      return `${prefix}${t('carousel.source_modal.page')} ${pageInfo}`;
    }
    
    return t('common.error');
  };

  // Determine content type info based on processing_mode
  const getContentTypeInfo = () => {
    const mode = post.processing_mode;

    // AI-generated modes
    if (mode === 'auto' || mode === 'narrative' || mode === 'explanatory') {
      return {
        label: t('upload.form.auto_detect.title'),
        icon: 'robot',
        color: 'bg-blue-50 text-blue-700',
        iconColor: 'text-blue-600'
      };
    }

    // Original text modes (user-initiated or fallback)
    if (mode === 'preserve' || mode === 'preserve_fallback') {
      return {
        label: t('upload.form.preserve.title'),
        icon: 'book',
        color: 'bg-green-50 text-green-700',
        iconColor: 'text-green-600'
      };
    }

    // Fallback for legacy posts or missing data
    return {
      label: `${t('upload.form.processing_type')}: Unknown`,
      icon: 'help',
      color: 'bg-gray-50 text-gray-700',
      iconColor: 'text-gray-600'
    };
  };

  // Call once and store result for efficient rendering
  const contentTypeInfo = getContentTypeInfo();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4 z-50">
      <div className="bg-white rounded-xl w-full max-w-sm sm:max-w-md max-h-[85vh] overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-gray-200">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            {t('carousel.source_modal.title')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-5 space-y-4">
          {/* Location Information */}
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="w-5 h-5 text-blue-600" />
              <span className="font-semibold text-blue-800 text-base">{t('carousel.source_modal.location')}</span>
            </div>
            <p className="text-blue-700 text-lg font-mono font-medium">{getLocationText()}</p>
          </div>

          {/* Content Type */}
          <div className={`rounded-lg p-4 ${contentTypeInfo.color}`}>
            <div className="flex items-center gap-2 mb-3">
              {contentTypeInfo.icon === 'robot' && <Bot className={`w-5 h-5 ${contentTypeInfo.iconColor}`} />}
              {contentTypeInfo.icon === 'book' && <Book className={`w-5 h-5 ${contentTypeInfo.iconColor}`} />}
              {contentTypeInfo.icon === 'help' && <HelpCircle className={`w-5 h-5 ${contentTypeInfo.iconColor}`} />}
              <span className="font-semibold text-base">{t('upload.form.processing_type')}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-white bg-opacity-80 rounded-full px-3 py-2">
                <span className="font-bold text-base">{contentTypeInfo.label}</span>
              </div>
            </div>
          </div>

          {/* Chapter Information */}
          {chapterInfo && (
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Book className="w-5 h-5 text-purple-600" />
                <span className="font-semibold text-purple-800 text-base">{t('navigation.chapters')}</span>
              </div>
              <p className="text-purple-700 text-base">{chapterInfo}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 sm:p-5">
          <button
            onClick={onClose}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium text-base"
          >
            {t('common.back')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SourceInfoModal;